import React, { useEffect, useState } from "react";
import { Menu as MenuIcon, Phone, X } from "lucide-react";
import LanguageSwitch from "../LanguageSwitch/LanguageSwitch";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { trackBookClick, trackPricesClick } from "../../analytics";

const SALON_NAME = "BEST GROOM STUDIO";

const SiteMenu: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const navItems = [
    { text: t("prices"), id: "pricesId" },
    { text: t("reviews"), id: "reviewsId" },
    { text: t("map"), id: "howToFindId" },
  ];

  const scrollTo = (id: string): void => {
    if (id === "pricesId") {
      trackPricesClick("nav");
    }
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    const onScroll = (): void => {
      setScrolled(window.scrollY >= 50);
    };
    window.addEventListener("scroll", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [mobileOpen]);

  const closeMobile = (): void => {
    setMobileOpen(false);
  };

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-40 transition-colors duration-300 ${
          scrolled ? "bg-mint shadow-sm" : "bg-cream"
        }`}
      >
        <div className="mx-auto flex max-w-[1200px] items-center justify-between px-2 py-2 sm:px-4 sm:py-3">
          <Link to="/" className="flex items-center gap-2 sm:gap-3">
            <img
              src="/logo.png?v=2"
              alt="Salon Logo"
              width={125}
              height={160}
              className="h-[35px] w-auto sm:h-[45px]"
            />
            <span
              className="text-[1.1rem] font-semibold text-body sm:text-[1.4rem]"
              style={{ fontFamily: "Recoleta, serif" }}
            >
              {SALON_NAME}
            </span>
          </Link>

          <nav
            className="hidden flex-1 justify-center gap-2 md:flex"
            aria-label="Primary"
          >
            {navItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  scrollTo(item.id);
                }}
                className="px-3 py-2 text-[0.95rem] font-medium text-body transition-colors hover:text-primary-deep"
              >
                {item.text}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-1 sm:gap-3">
            <Link
              to="/appointments"
              className="hidden rounded-full bg-primary px-5 py-2 text-[0.95rem] font-medium text-white shadow-lg shadow-primary/25 transition-colors hover:bg-primary-hover md:inline-flex"
              onClick={() => {
                trackBookClick("nav");
              }}
            >
              {t("header.bookAppointment")}
            </Link>

            <LanguageSwitch />

            <button
              type="button"
              className="inline-flex rounded-lg p-2 text-body md:hidden"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              onClick={() => {
                setMobileOpen((open) => !open);
              }}
            >
              {mobileOpen ? <X size={24} /> : <MenuIcon size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Spacer for fixed header */}
      <div className="h-[72px] bg-cream sm:h-[84px]" aria-hidden="true" />

      {mobileOpen && (
        <div
          className="fixed inset-0 z-50 md:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Mobile menu"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close menu"
            onClick={closeMobile}
          />
          <div className="absolute inset-y-0 right-0 flex w-[min(100%,280px)] flex-col bg-surface shadow-2xl">
            <div className="flex items-center justify-between border-b border-black/5 px-4 py-3">
              <img
                src="/logo.png?v=2"
                alt=""
                width={125}
                height={160}
                className="h-12 w-auto"
              />
              <button
                type="button"
                className="rounded-lg p-2 text-body"
                aria-label="Close menu"
                onClick={closeMobile}
              >
                <X size={22} />
              </button>
            </div>

            <nav className="flex flex-col gap-1 px-3 py-4" aria-label="Mobile">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="rounded-lg px-4 py-3 text-left text-base font-medium text-body hover:bg-mint/40"
                  onClick={() => {
                    scrollTo(item.id);
                    closeMobile();
                  }}
                >
                  {item.text}
                </button>
              ))}
            </nav>

            <div className="px-4">
              <Link
                to="/appointments"
                onClick={() => {
                  trackBookClick("mobile_menu");
                  closeMobile();
                }}
                className="flex w-full items-center justify-center rounded-full bg-primary px-5 py-3 text-base font-medium text-white shadow-lg shadow-primary/30 transition-colors hover:bg-primary-hover"
              >
                {t("header.bookAppointment")}
              </Link>
            </div>

            <div className="mt-auto border-t border-black/5 px-4 py-5">
              <a
                href="tel:+48574516116"
                className="inline-flex items-center gap-2 text-sm text-body"
              >
                <Phone size={18} className="text-primary" />
                +48 574 516 116
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SiteMenu;
